/* eslint-disable jsx-a11y/anchor-is-valid */
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ENDPOINT } from "../../../services/api-endpoint";
import { getList, getLists, uploadOrderCadFile } from "../../../services/services";
import AppLoader from "../../../components/AppLoader";
import { usePageLoader } from "../../../services/usePageLoader";
import type { UiOrder } from "../mes-order-utils";
import { normalizeManualOrder, sortOrdersByCreatedDesc } from "../mes-order-utils";
import Moment from "react-moment";
import "./cad-manufacturing.scss";

/** Display badges — matches supported CAD families (STEP, IGES, STL, DXF, DWG, Parasolid, CATIA, SolidWorks). */
const CAD_BADGES = [".STEP", ".IGES", ".STL", ".DXF", ".DWG", ".X_T"];

const ACCEPT_ATTR =
  ".step,.stp,.iges,.igs,.stl,.dxf,.dwg,.x_t,.x_b,.catpart,.catproduct,.sldprt,.sldasm,.model,.session";

const CadManufacturing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const orderFromNav = (location.state as { order?: UiOrder })?.order;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [awaiting, setAwaiting] = useState<UiOrder[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<UiOrder | null>(null);
  const [clientList, setClientList] = useState<any[]>([]);
  const { isLoading, runWithLoading } = usePageLoader();

  const clientLabel = useCallback(
    (id: string | number | null | undefined) => {
      const c = clientList.find((x) => String(x.id) === String(id));
      return c?.companyorgname || c?.clientname || `Client #${id ?? "—"}`;
    },
    [clientList],
  );

  const loadAwaiting = useCallback(async () => {
    await runWithLoading(async () => {
      const list = await getLists(ENDPOINT.ORDER_LIST, {
        pagesize: 200,
        pagenumber: 0,
        serachtext: "",
      });
      const rows = list?.data || [];
      const ui = rows.map((r: any) => normalizeManualOrder(r, clientLabel));
      const sorted = sortOrdersByCreatedDesc(ui);
      setAwaiting(sorted);
      setSelectedOrder((sel) => {
        if (!sel) return sel;
        const next = sorted.find((x) => x.id === sel.id);
        return next ?? sel;
      });
    });
  }, [clientLabel, runWithLoading]);

  useEffect(() => {
    (async () => {
      const clients = await getList(ENDPOINT.CLIENT_LIST);
      setClientList(Array.isArray(clients) ? clients : []);
    })();
  }, []);

  useEffect(() => {
    loadAwaiting();
  }, [loadAwaiting]);

  useEffect(() => {
    if (orderFromNav?.source === "manual" && orderFromNav.id) {
      setSelectedOrder(orderFromNav);
    }
  }, [orderFromNav]);

  const goOrder = (o: UiOrder, e?: React.MouseEvent) => {
    e?.stopPropagation();
    navigate(`/mes-order/order/${o.id}`, { state: { order: o } });
  };

  const selectOrder = (o: UiOrder) => {
    setSelectedOrder(o);
  };

  const handleUploadFile = async (file: File | undefined | null) => {
    if (!file?.size) {
      toast.warning("Choose a CAD file to upload.");
      return;
    }
    if (!selectedOrder) {
      toast.warning("Select a manual order from the list first (click a row).");
      return;
    }

    try {
      await runWithLoading(async () => {
        await uploadOrderCadFile(selectedOrder.id, file);
        toast.success("CAD file uploaded and linked to this order.");
        await loadAwaiting();
      });
    } catch {
      toast.error("Upload failed. Check file type and try again.");
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const statusClass = (s: string) => {
    if (s === "In Progress") return "tv-cad-row-status tv-cad-row-status--progress";
    if (s === "Open") return "tv-cad-row-status tv-cad-row-status--open";
    return "tv-cad-row-status tv-cad-row-status--draft";
  };

  return (
    <div>
      {isLoading && <AppLoader text="Loading..." />}
      <div className="tv-page-content tv-cad-page">
          <button
            type="button"
            className="btn btn-link tv-cad-back pl-0"
            onClick={() => navigate("/mes-order")}
          >
            <i className="fa fa-arrow-left mr-2" aria-hidden="true" />
            Back to Orders
          </button>

          <p className="tv-cad-sub">
            Upload CAD files for manual orders to extract CAM operations, inspection plans, and bill
            of process.
          </p>

          <div className="tv-cad-alert" role="status">
            <i className="fa fa-info-circle tv-cad-alert-icon" aria-hidden="true" />
            <div>
              Orders placed through the <strong>Truventor platform</strong> are processed
              automatically. This area is for <strong>manual orders</strong> that need CAD uploaded
              separately.
            </div>
          </div>

          <div className="tv-cad-card">
            <div className="tv-cad-card-head">
              <i className="fa fa-cloud-upload mr-2" aria-hidden="true" />
              Manual Orders Awaiting CAD Upload
            </div>
            <ul className="tv-cad-await-list list-unstyled mb-0">
              {awaiting.map((o) => (
                <li
                  key={o.id}
                  className={`tv-cad-await-row ${
                    selectedOrder?.id === o.id ? "tv-cad-await-row--selected" : ""
                  }`}
                  onClick={() => selectOrder(o)}
                  onKeyDown={(e) => e.key === "Enter" && selectOrder(o)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="tv-cad-await-left">
                    <span className="tv-pill tv-pill--source-manual">
                      <i className="fa fa-upload mr-1" aria-hidden="true" />
                      Manual
                    </span>
                    <span className="tv-cad-await-code">{o.ordercode}</span>
                    <span className={statusClass(o.status)}>{o.status}</span>
                  </div>
                  <div className="tv-cad-await-mid text-muted small">
                    Due:{" "}
                    <Moment format="D MMM YYYY">{o.duedate || o.createdate}</Moment>
                  </div>
                  <div className="tv-cad-await-right d-flex align-items-center gap-2">
                    <span className="text-muted small">
                      {o.cadFileUrl ? (
                        <span className="text-success">CAD file attached</span>
                      ) : (
                        "No CAD file attached"
                      )}
                    </span>
                    <button
                      type="button"
                      className="btn btn-link btn-sm py-0 tv-cad-view-order"
                      onClick={(e) => goOrder(o, e)}
                    >
                      View order
                    </button>
                  </div>
                </li>
              ))}
              {awaiting.length === 0 && !isLoading && (
                <li className="py-4 text-center text-muted">No manual orders pending CAD.</li>
              )}
            </ul>
          </div>

          <div
            className={`tv-cad-dropzone ${selectedOrder ? "tv-cad-dropzone--ready" : ""}`}
            onClick={() => selectedOrder && fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const f = e.dataTransfer.files?.[0];
              void handleUploadFile(f);
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              e.key === "Enter" && selectedOrder && fileInputRef.current?.click()
            }
          >
            <input
              ref={fileInputRef}
              type="file"
              className="d-none"
              accept={ACCEPT_ATTR}
              onChange={(e) => void handleUploadFile(e.target.files?.[0])}
            />
            <i className="fa fa-cloud-upload tv-cad-drop-icon" aria-hidden="true" />
            <h4 className="tv-cad-drop-title">Upload CAD File for Manual Order</h4>
            {selectedOrder ? (
              <p className="tv-cad-selected-order mb-2">
                Uploading for order <strong>{selectedOrder.ordercode}</strong> (ID:{" "}
                {selectedOrder.id})
              </p>
            ) : (
              <p className="text-muted small mb-2">
                Click an order in the list above, then select or drop a file here.
              </p>
            )}
            <p className="tv-cad-drop-formats">
              Supports STEP, IGES, STL, DXF, DWG, Parasolid, CATIA, SolidWorks
            </p>
            <div className="tv-cad-format-tags">
              {CAD_BADGES.map((t) => (
                <span key={t} className="tv-cad-format-tag">
                  {t}
                </span>
              ))}
            </div>
            <button
              type="button"
              className="btn btn-primary tv-list-add-btn mt-3"
              disabled={!selectedOrder}
              onClick={(e) => {
                e.stopPropagation();
                if (!selectedOrder) {
                  toast.info("Select an order from the list first.");
                  return;
                }
                fileInputRef.current?.click();
              }}
            >
              <i className="fa fa-upload mr-2" aria-hidden="true" />
              Select File
            </button>
          </div>
        </div>
    </div>
  );
};

export default CadManufacturing;
